import { Button, IconButton, Stack, Typography } from '@mui/material';
import { getFullName } from '@provision/formatting-utils';
import dayjs, { Dayjs } from 'dayjs';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_TableOptions,
  useMaterialReactTable,
} from 'material-react-table';
import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { DynamicIcon, Tab, Tabs, Tooltip } from 'src/components';
import { DATE_TIME_FORMAT } from 'src/constants/date';
import {
  useDeletePartnerHoliday,
  useGroupRules,
  usePartnerHolidays,
  UserGroupRules,
} from 'src/graphql';
import { PartnerHolidayEntity } from 'src/graphql/entities/partner-holiday/partner-holiday.types';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';

type HolidaysByTime = 'upcoming-holiday' | 'past-holiday';

export const PartnerHolidaysTable = () => {
  const currentPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showModal: openScheduleHolidayModal } = useModalMethods(Modals.ScheduleHolidays);
  const { checkValidity } = useGroupRules();
  const canManageHolidays = checkValidity([UserGroupRules.AccessToHolidaysSchedule]);

  const [holidaysByTimeCategory, setHolidaysByTimeCategory] =
    useState<HolidaysByTime>('upcoming-holiday');
  const onHolidayByTimeCategoryChange = useCallback(
    (_: SyntheticEvent, value: HolidaysByTime) =>
      setHolidaysByTimeCategory(value || holidaysByTimeCategory),
    [holidaysByTimeCategory],
  );

  const filters = useMemo(() => {
    const today = dayjs().startOf('day').format(DATE_TIME_FORMAT);
    const filters = [];

    switch (holidaysByTimeCategory) {
      case 'upcoming-holiday':
        filters.push({
          attribute: 'holidayEnd',
          operator: '>=',
          value: today,
        });
        break;
      case 'past-holiday':
        filters.push({
          attribute: 'holidayEnd',
          operator: '<',
          value: today,
        });
        break;
    }
    return filters;
  }, [holidaysByTimeCategory]);

  const { partnerHolidays, loading: isLoading } = usePartnerHolidays(
    {
      partnerId: currentPartnerId ?? undefined,
      filters,
    },
    { skip: isNullish(currentPartnerId) },
  );

  const { deletePartnerHolidaysById } = useDeletePartnerHoliday();

  const onDelete = useCallback(
    (value: PartnerHolidayEntity) => async () => {
      await deletePartnerHolidaysById([value.partnerHolidayId]);
    },
    [deletePartnerHolidaysById],
  );
  const onUpdate = useCallback(
    (value: PartnerHolidayEntity) => () => {
      openScheduleHolidayModal({
        updateExisting: true,
        value,
      });
    },
    [openScheduleHolidayModal],
  );

  const isFullDay = (value: PartnerHolidayEntity) => {
    const isFullStartDay = value.holidayStart.startOf('day').isSame(value.holidayStart);
    const isFullEndDay = value.holidayEnd.endOf('day').isSame(value.holidayEnd);
    return isFullStartDay && isFullEndDay;
  };

  const formatDate = useCallback((date: Dayjs, value: PartnerHolidayEntity) => {
    const dateFormat = isFullDay(value) ? 'MMM D, YYYY' : 'MMM D, YYYY [at] H:mm';
    return date.format(dateFormat);
  }, []);

  const columns = useMemo(
    (): MRT_ColumnDef<PartnerHolidayEntity>[] => [
      {
        accessorKey: 'holidayName',
        header: 'Holiday Name',
      },
      {
        accessorKey: 'holidayStart',
        header: 'Start At',
        Cell: ({ row }) => (
          <Typography>{formatDate(row.original.holidayStart, row.original)}</Typography>
        ),
        enableSorting: true,
        sortingFn: 'datetime',
      },
      {
        accessorKey: 'holidayEnd',
        header: 'Ends At',
        Cell: ({ row }) => (
          <Typography>{formatDate(row.original.holidayEnd, row.original)}</Typography>
        ),
        enableSorting: true,
        sortingFn: 'datetime',
      },
      {
        id: 'type',
        header: 'Type',
        accessorFn: (value) => {
          if (!value) {
            return '';
          }

          return isFullDay(value) ? 'Full Day' : 'Partial';
        },
        enableSorting: true,
      },
      {
        accessorKey: 'submitterId',
        accessorFn: (value) => getFullName(value.platformUser),
        header: 'Added By',
        enableSorting: true,
      },
    ],
    [formatDate],
  );

  const config = useMemo((): MRT_TableOptions<PartnerHolidayEntity> => {
    return {
      data: partnerHolidays,
      columns,
      getRowId: (row) => row.partnerHolidayId,
      positionActionsColumn: 'last',
      enableRowActions: canManageHolidays,
      renderRowActions: canManageHolidays
        ? ({ row }) => (
            <Stack direction="row" spacing={1}>
              <Tooltip title="Edit">
                <IconButton aria-label="edit" color="info" onClick={onUpdate(row.original)}>
                  <DynamicIcon name="pencil" library="lucide" size={24} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton aria-label="delete" color="error" onClick={onDelete(row.original)}>
                  <DynamicIcon name="trash-2" library="lucide" size={24} />
                </IconButton>
              </Tooltip>
            </Stack>
          )
        : undefined,
      defaultColumn: {
        enableSorting: true,
        enableColumnActions: false,
      },
      enableBottomToolbar: false,
      state: {
        showProgressBars: isLoading,
        isLoading,
      },
      initialState: {
        sorting: [
          {
            id: 'holidayStart',
            desc: false,
          },
        ],
      },
      enableGlobalFilter: true,
      globalFilterFn: 'contains',
      muiSearchTextFieldProps: {
        placeholder: 'Filter results',
      },
      enableColumnFilters: false,
      muiTableContainerProps: {
        sx: {
          // Clamp function is modification of original one (`clamp(350px, 0px + 100vh, 9999px)`).
          // It's changed to occupy 100% of space instead of 100% of view port.
          maxHeight: 'clamp(350px, 0px + 100%, 9999px)',
        },
      },
      muiTableHeadCellProps: {
        sx: (theme) => ({
          backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
        }),
      },
      muiTablePaperProps: {
        elevation: 0,
        sx: {
          height: '100%',
        },
      },
      enableStickyHeader: true,
      enableSorting: true,
      enableFullScreenToggle: false,
      enableDensityToggle: false,
      enableHiding: false,
      enablePagination: false,
      renderTopToolbarCustomActions: () => (
        <Tabs value={holidaysByTimeCategory} onChange={onHolidayByTimeCategoryChange}>
          <Tab label="Upcoming Holidays" value="upcoming-holiday" />
          <Tab label="Past Holidays" value="past-holiday" />
        </Tabs>
      ),
    };
  }, [
    partnerHolidays,
    columns,
    canManageHolidays,
    isLoading,
    onUpdate,
    onDelete,
    holidaysByTimeCategory,
    onHolidayByTimeCategoryChange,
  ]);

  const table = useMaterialReactTable(config);
  return (
    <Stack spacing={1}>
      {canManageHolidays ? (
        <Stack direction="row" justifyContent="right">
          <Button
            variant="contained"
            onClick={() => {
              openScheduleHolidayModal({
                updateExisting: false,
              });
            }}
          >
            Add Holiday
          </Button>
        </Stack>
      ) : null}
      <MaterialReactTable table={table} />
    </Stack>
  );
};
